import { useMemo, useState } from 'react';
import { Box, Icon, Input, Text } from '@nimbus-ds/components';
import { SearchIcon } from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import { useNavegate } from 'App/hooks';
import { CONFIGURATIONS_ROUTES } from 'domains/Configurations/configurationsRoutes';
import {
  useGetDomainSuggestionsQuery,
  useCreateDomainOrderMutation,
} from 'domains/Configurations/Domains/domainsApi';
import { useDomainSuggestionByName } from 'domains/Configurations/Domains/hooks';
import { formatDomainPrice } from 'domains/Configurations/Domains/utils';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import {
  DomainSuggestionCard,
  DomainSuggestionRow,
  DomainSuggestionRowSkeleton,
  DomainUnavailableCard,
} from './components';
import { useSearchDomain } from './hooks/useSearchDomain';
import { SearchNewDomainSkeleton } from './SearchNewDomainSkeleton';

interface SearchNewDomainProps {
  initialSearchQuery?: string;
}

export function SearchNewDomain({
  initialSearchQuery,
}: Readonly<SearchNewDomainProps>) {
  const t = useTranslationConfigurations(
    'domains.landing.addDomainModal.searchNewDomain',
  );
  const { goTo, buildPath } = useNavegate();

  const {
    inputValue,
    searchQuery,
    handleSearchChange,
    handleKeyDown,
    handleBlur,
  } = useSearchDomain(initialSearchQuery);

  const [purchasingDomain, setPurchasingDomain] = useState<string | null>(null);
  const [createDomainOrder] = useCreateDomainOrderMutation();

  const {
    recommendation: initialRecommendation,
    suggestions: initialSuggestions,
    isLoading: isLoadingInitial,
  } = useDomainSuggestionByName();

  const {
    data: {
      recommendation: searchRecommendation,
      suggestions: searchSuggestions,
    } = {},
    isFetching: isFetchingSearch,
  } = useGetDomainSuggestionsQuery(
    { query: searchQuery },
    { skip: !searchQuery },
  );

  const hasCustomSearch = searchQuery.length > 0;

  const recommendation = useMemo(
    () => (hasCustomSearch ? searchRecommendation : initialRecommendation),
    [hasCustomSearch, searchRecommendation, initialRecommendation],
  );

  const suggestions = useMemo(
    () => (hasCustomSearch ? searchSuggestions : initialSuggestions),
    [hasCustomSearch, searchSuggestions, initialSuggestions],
  );

  const handleBuyDomain = async (domainName: string) => {
    setPurchasingDomain(domainName);
    try {
      const result = await createDomainOrder({ domainName }).unwrap();

      if (result.payOrderId) {
        goTo(
          buildPath(CONFIGURATIONS_ROUTES.domainsCheckout, {
            payOrderId: result.payOrderId,
          }),
        );
      }
    } catch {
      // El error es manejado por RTK Query
    } finally {
      setPurchasingDomain(null);
    }
  };

  const isPurchasing = purchasingDomain !== null;

  const isLoading = hasCustomSearch ? isFetchingSearch : isLoadingInitial;

  if (isLoadingInitial) return <SearchNewDomainSkeleton />;

  return (
    <Box display="flex" flexDirection="column">
      <Box padding="4" paddingTop="none">
        <Input
          append={<Icon source={<SearchIcon />} />}
          appendPosition="start"
          placeholder={t('inputPlaceholder')}
          value={inputValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          autoFocus
        />
      </Box>
      {!isLoading &&
        hasCustomSearch &&
        recommendation &&
        !recommendation.availability.available && (
          <Box padding="4" paddingTop="none">
            <DomainUnavailableCard
              reason={recommendation.availability.reason}
              domain={recommendation.domainName}
            />
          </Box>
        )}
      {!isLoading && recommendation?.availability.available && (
        <Box padding="4" paddingTop="none">
          <DomainSuggestionCard
            domain={recommendation.domainName}
            price={formatDomainPrice(recommendation.price)}
            onBuyDomain={handleBuyDomain}
            disabled={isPurchasing}
            isLoading={purchasingDomain === recommendation.domainName}
          />
        </Box>
      )}

      <Box display="flex" flexDirection="column" paddingTop="4">
        <Box padding="4" paddingTop="none">
          <Text color="neutral-textHigh">{t('suggestions')}</Text>
        </Box>
        <DataList>
          {isLoading ? (
            <DomainSuggestionRowSkeleton />
          ) : (
            suggestions &&
            suggestions.map((suggestion) => (
              <DomainSuggestionRow
                key={suggestion.domainName}
                domain={suggestion.domainName}
                price={formatDomainPrice(suggestion.price)}
                onBuyDomain={handleBuyDomain}
                disabled={isPurchasing}
                isLoading={purchasingDomain === suggestion.domainName}
              />
            ))
          )}
        </DataList>
      </Box>
    </Box>
  );
}
